import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Swords, ChevronRight, BookOpenText } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2J — Rivalries card (renders on squad detail page).
 * Surfaces all rivalries this squad has accumulated.
 * Tone: competitive + tribal, never aggressive.
 *
 * Phase 2N PR 5 — Rivalries with 3+ meetings get a "Read documentary" CTA.
 */
export default function ArenaxRivalriesCard({ squadId, squadName }) {
  const [data, setData] = useState({ rivalries: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!squadId) return;
    api.get(`/arenax/squads/${squadId}/rivalries?min_matches=1&limit=10`)
      .then(r => setData(r.data || { rivalries: [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [squadId]);

  if (loading || data.rivalries.length === 0) return null;

  return (
    <div className="rounded-2xl border border-rose-500/20 p-5"
      data-testid="arenax-rivalries-card"
      style={{ background: 'linear-gradient(160deg, rgba(244,63,94,0.05), var(--ax-card-bg-soft))' }}>
      <div className="flex items-center gap-2 mb-4">
        <Swords className="h-4 w-4 text-rose-300" />
        <span className="text-[10px] tracking-[0.3em] text-rose-300 font-bold uppercase">Rivalries</span>
      </div>
      <div className="space-y-3">
        {data.rivalries.slice(0, 5).map(r => (
          <RivalryRow key={r.opponent_id} rivalry={r} squadName={squadName} squadId={squadId} />
        ))}
      </div>
    </div>
  );
}

function RivalryRow({ rivalry, squadName, squadId }) {
  const ledColor = rivalry.streak > 0 ? '#10b981' : rivalry.streak < 0 ? '#f43f5e' : '#94a3b8';
  const hasDoc = (rivalry.matches_count || 0) >= 3;
  return (
    <div className="rounded-lg p-3 bg-slate-900/40 border border-slate-800"
      data-testid={`arenax-rivalry-row-${rivalry.opponent_id}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-bold text-slate-100 truncate">{rivalry.opponent_name}</span>
          {rivalry.streak !== 0 && (
            <span className="text-[9px] uppercase tracking-wider font-bold tabular-nums"
              style={{ color: ledColor }}>
              {rivalry.streak > 0 ? `W${rivalry.streak}` : `L${Math.abs(rivalry.streak)}`}
            </span>
          )}
        </div>
        <span className="text-sm font-black tabular-nums text-slate-300">
          <span className="text-emerald-300">{rivalry.wins}</span>
          <span className="text-slate-600 mx-1">·</span>
          <span className="text-slate-400">{rivalry.draws}</span>
          <span className="text-slate-600 mx-1">·</span>
          <span className="text-rose-300">{rivalry.losses}</span>
        </span>
      </div>
      {rivalry.narrative && (
        <p className="text-[11px] text-slate-400 leading-snug italic" data-testid={`arenax-rivalry-narrative-${rivalry.opponent_id}`}>
          {rivalry.narrative}
        </p>
      )}
      {rivalry.last_match_label && (
        <p className="text-[10px] text-slate-500 mt-1">
          Last: {rivalry.last_match_label}
        </p>
      )}
      {hasDoc && (
        <Link to={`/arenax/rivalries/${squadId}/${rivalry.opponent_id}/documentary`}
          className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-rose-300
                       hover:text-rose-200 hover:translate-x-0.5 transition-all"
          data-testid={`arenax-rivalry-doc-link-${rivalry.opponent_id}`}>
          <BookOpenText className="h-3 w-3" />
          Read the documentary
          <ChevronRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}
